import { Card } from 'antd'
import React from 'react'
import numeral from 'numeral'
import { PolicyDataCamel } from '../../..'

interface PolicyCostDetailProps {
    policyData: PolicyDataCamel[]
}

const formatNumber = (value: number) => {
    if (value === 0) return '-'
    return numeral(value).format('(0,0)')
}

const formatPercent = (value: number) => {
    if (value === 0) return '-'
    return numeral(value).format('0,0.00%')
}

const PolicyCostDetail = (props: PolicyCostDetailProps) => {
    const { policyData } = props

    let totalLoan = 0
    let totalPremiums = 0
    let totalCharges = 0
    let totalCredits = 0

    let accumulationValue = 0
    let accumulationRow = policyData.find((row) => row.policyYear === 20)
    if (accumulationRow !== undefined) {
        accumulationValue = accumulationRow.nonGuaranteedAccumulationValue
    }

    return (
        <Card title="Policy Cost Detail">
            <table className="atap-policy-cost-detail">
                <thead>
                    <tr>
                        <th>Year</th>
                        <th>Age</th>
                        <th>
                            Annual
                            <br />
                            Premium
                        </th>
                        <th>
                            Annual
                            <br />
                            Loan
                        </th>
                        <th>Total Loan</th>
                        <th>
                            Account
                            <br />
                            Value
                        </th>
                        <th>
                            Surrender
                            <br />
                            Value
                        </th>
                        <th>
                            Death
                            <br />
                            Benefit
                        </th>
                        <th>
                            Total
                            <br />
                            Charges
                        </th>
                        <th>
                            Annual
                            <br />
                            Policy
                            <br />
                            Credits
                        </th>
                        <th className="col-fill-aria-yellow">
                            Charges Net of
                            <br />
                            Annual Policy
                            <br />
                            Credits
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {policyData.map((row) => {
                        if (row.policyYear <= 20) {
                            const annualCharges =
                                row.premiumCharge +
                                row.coiCharge +
                                row.policyIssueCharge +
                                row.additionalCharges
                            const annualCredits =
                                row.bonusInterestCredit +
                                row.additionalPolicyCredits

                            totalLoan += row.annualLoanAmount
                            totalPremiums += row.premiumOutlay
                            totalCharges += annualCharges
                            totalCredits += annualCredits

                            return (
                                <tr key={row.policyYear}>
                                    <td>{row.policyYear}</td>
                                    <td>{row.beginningOfYearAge}</td>
                                    <td>{formatNumber(row.premiumOutlay)}</td>
                                    <td>
                                        {formatNumber(row.annualLoanAmount)}
                                    </td>
                                    <td>{formatNumber(totalLoan)}</td>
                                    <td>
                                        {formatNumber(
                                            row.nonGuaranteedAccumulationValue
                                        )}
                                    </td>
                                    <td>
                                        {formatNumber(
                                            row.nonGuaranteedSurrenderValue
                                        )}
                                    </td>
                                    <td>
                                        {formatNumber(
                                            row.nonGuaranteedDeathBenefit
                                        )}
                                    </td>
                                    <td>{formatNumber(annualCharges)}</td>
                                    <td>{formatNumber(annualCredits)}</td>
                                    <td className="col-fill-aria-yellow">
                                        {formatNumber(
                                            annualCharges + annualCredits
                                        )}
                                    </td>
                                </tr>
                            )
                        }
                    })}
                    <tr>
                        <td colSpan={10}></td>
                        <td className="col-fill-aria-yellow"></td>
                    </tr>
                    <tr>
                        <td colSpan={10}></td>
                        <td className="col-fill-aria-yellow"></td>
                    </tr>
                    <tr>
                        <td></td>
                        <th>Total</th>
                        <td className="text-bold">
                            {formatNumber(totalPremiums)}
                        </td>
                        <td colSpan={5}></td>
                        <td>{formatNumber(totalCharges)}</td>
                        <td>{formatNumber(totalCredits)}</td>
                        <td className="col-fill-aria-yellow">
                            {formatNumber(totalCharges + totalCredits)}
                        </td>
                    </tr>
                </tbody>
                <tfoot>
                    <tr>
                        <th colSpan={4}>Total % of Total Premium (20 Years)</th>
                        <td colSpan={4}></td>
                        <td>{formatPercent(totalCharges / totalPremiums)}</td>
                        <td>{formatPercent(totalCredits / totalPremiums)}</td>
                        <td className="col-fill-aria-yellow">
                            {formatPercent(
                                (totalCharges + totalCredits) / totalPremiums
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th colSpan={3} className="col-fill-aria-green">
                            Annual % of Total Premium
                            <br />
                            (Total % / 20 Years)
                        </th>
                        <td colSpan={5}></td>
                        <td className="col-fill-aria-green">
                            {formatPercent(totalCharges / totalPremiums / 20)}
                        </td>
                        <td className="col-fill-aria-green">
                            {formatPercent(totalCredits / totalPremiums / 20)}
                        </td>
                        <td className="col-fill-aria-green text-bold">
                            {formatPercent(
                                (totalCharges + totalCredits) /
                                    totalPremiums /
                                    20
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th colSpan={4}>Total % of Account Value (20 Years)</th>
                        <td colSpan={4}></td>
                        <td>
                            {formatPercent(totalCharges / accumulationValue)}
                        </td>
                        <td>
                            {formatPercent(totalCredits / accumulationValue)}
                        </td>
                        <td className="text-bold">
                            {formatPercent(
                                (totalCharges + totalCredits) /
                                    accumulationValue
                            )}
                        </td>
                    </tr>
                    <tr>
                        <th colSpan={3} className="col-fill-aria-blue">
                            Annual % of Account Value
                            <br />
                            (Total % / 20 Years)
                        </th>
                        <td colSpan={5}></td>
                        <td className="col-fill-aria-blue">
                            {formatPercent(
                                totalCharges / accumulationValue / 20
                            )}
                        </td>
                        <td className="col-fill-aria-blue">
                            {formatPercent(
                                totalCredits / accumulationValue / 20
                            )}
                        </td>
                        <td className="col-fill-aria-blue text-bold">
                            {formatPercent(
                                (totalCharges + totalCredits) /
                                    accumulationValue /
                                    20
                            )}
                        </td>
                    </tr>
                </tfoot>
            </table>
            <br />
            <span className="disclaimer">
                Index returns assumed level at 5.43%. Please refer to the
                attached illustration for details.
                <br />
                <br />
                Hypothetical example is for illustrative purposes only. This is
                not a prediction or guarantee of actual results, which will vary
                from those described.
                <br />
                This illustration assumes that the currently illustrated
                non‐guaranteed elements will remain unchanged for all years
                shown. This is not likely to occur and actual results may be
                more or
                <br />
                less favorable than those shown. Administrative and insurance
                charges are deducted every month regardless of whether premium
                outlays are made. Depending upon actual policy experience, the
                Owner may need to increase premium payments to maintain coverage
                for the desired duration. Any policy loans and partial
                surrenders will affect policy values and may require additional
                premiums to avoid policy termination.
                <br />
                Life insurance guarantees rely on the fiscal strength and claims
                paying ability of the issuing insurer. Universal life insurance
                products are not bank or FDIC insured. Indexed Universal Life
                insurance products are not an investment in the stock market and
                are subject to all policy fees and charges associated with
                Universal Life policies.
                <br />
                ARIA Financial Software, LLC does not provide tax, legal or
                accounting advice, nor is it designed ‐ nor intended ‐ to be
                appliable to any person's individual circumstances. This
                material has been prepared for information purposes only, and is
                not intended to provide, and should not be relied on for, tax,
                legal or accounting advice. You should consult your own
                qualified tax, legal and accounting advisors before engaging in
                any transaction.
                <br />
            </span>
        </Card>
    )
}

export default PolicyCostDetail
